import React from 'react';
import _ from 'lodash';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  cohortName: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.cohortName',
    defaultMessage: 'Cohort name',
  },
  cohortCancerType: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.cohortCancerType',
    defaultMessage: 'Cohort Cancer Type',
  },
  cohortDetails: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.cohortDetails',
    defaultMessage: 'Cohort Details',
  },
  percentSamplesCohort: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.percentSamplesCohort',
    defaultMessage: '% Samples',
  },
  role: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.role',
    defaultMessage: 'Role',
  },
  methods: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.methods',
    defaultMessage: 'Methods',
  },
  type: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.type',
    defaultMessage: 'Type',
  },
  platform: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.platform',
    defaultMessage: 'Platform',
  },
  age: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.age',
    defaultMessage: 'Age',
  },
  samples: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.samples',
    defaultMessage: 'Samples with Mutation',
  },
  cohortSamples: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.cohortSamples',
    defaultMessage: 'Cohort Samples',
  },
  treated: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.treated',
    defaultMessage: 'Treated',
  },
  reference: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.reference',
    defaultMessage: 'Reference',
  },
  qvalueCombination: {
    id: 'omicsExplorer.variantDetails.dialog.diseastTable.qvalueCombination',
    defaultMessage: 'Q Value Combination',
  },
});

const maybeRenderTooltipContent = ({ cell, valuePath, length = 21 }) => {
  if (!cell || !valuePath) {
    return null;
  }

  const content = cell[valuePath];

  if (content && content.length > length) {
    return (
      <Tooltip title={content} placement="bottom">
        {/* Tooltip needs an element that can hold a ref */}
        <span>{content}</span>
      </Tooltip>
    );
  }

  return content;
};

const columns = formatMessage => [
  {
    header: {
      label: formatMessage(messages.cohortDetails),
    },
    cell: {
      content: cell =>
        maybeRenderTooltipContent({ cell, valuePath: 'cohortDetails' }),
    },
  },
  {
    header: {
      label: formatMessage(messages.cohortCancerType),
    },
    cell: {
      valuePath: 'cohortCancerType',
    },
  },
  {
    header: {
      label: formatMessage(messages.percentSamplesCohort),
    },
    cell: {
      content: value =>
        value.percentSamplesCohort &&
        (parseFloat(value.percentSamplesCohort, 10) * 100).toFixed(2),
    },
  },
  {
    header: {
      label: formatMessage(messages.samples),
    },
    cell: {
      valuePath: 'samples',
    },
  },
  {
    header: {
      label: formatMessage(messages.cohortSamples),
    },
    cell: {
      valuePath: 'cohortSamples',
    },
  },
  {
    header: {
      label: formatMessage(messages.role),
    },
    cell: {
      valuePath: 'role',
    },
  },
  {
    header: {
      label: formatMessage(messages.methods),
    },
    cell: {
      content: cell =>
        maybeRenderTooltipContent({ cell, valuePath: 'methods' }),
    },
  },
  {
    header: {
      label: formatMessage(messages.type),
    },
    cell: {
      valuePath: 'cohortType',
    },
  },
  {
    header: {
      label: formatMessage(messages.platform),
    },
    cell: {
      valuePath: 'cohortPlatform',
    },
  },
  {
    header: {
      label: formatMessage(messages.age),
    },
    cell: {
      valuePath: 'cohortAge',
    },
  },
  {
    header: {
      label: formatMessage(messages.treated),
    },
    cell: {
      valuePath: 'cohortTreated',
    },
  },
  {
    header: {
      label: formatMessage(messages.reference),
    },
    cell: {
      content: cell =>
        maybeRenderTooltipContent({ cell, valuePath: 'cohortReference' }),
    },
  },
];

const DiseaseTable = ({ contentClassName, data }) => {
  const { formatMessage } = useIntl();

  return (
    <div className={contentClassName}>
      <TableModule
        config={columns(formatMessage)}
        data={_.orderBy(data, ['percentSamplesCohort'], ['desc'])}
        maxCellWidth={2}
      />
    </div>
  );
};

export default DiseaseTable;
